import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { getAllSchemeSpecificLoginDetails } from "../utils/healthCareSliceFunc";
import {
  derivePortalUse,
  parseInsuranceSetupMeta,
} from "../utils/insuranceSetupMapper";
import { InsurancePortalUse } from "../interfaces/IInsuranceSetup";
import {
  formatStageLabel,
  getFallbackPortalUrl,
  normalizePortalName,
} from "../utils/tpaPortalUrls";

export type TpaPortalPatientContext = {
  patientName: string;
  uhid: string;
  crNo: string;
  doa: string;
  claimId: string;
};

export type TpaPortalConfig = {
  portalUrl: string;
  portalName: string;
  schemeName: string;
  userName: string | null;
  stageLabel: string;
  usedFallbackUrl: boolean;
  portalUse: InsurancePortalUse;
  patientContext: TpaPortalPatientContext;
};

type UseTpaPortalConfigArgs = {
  visitStageName?: string | null;
  enabled?: boolean;
};

export function useTpaPortalConfig({
  visitStageName,
  enabled = true,
}: UseTpaPortalConfigArgs): {
  config: TpaPortalConfig | null;
  loading: boolean;
  /** EMAIL setups skip portal open UI entirely */
  skipPortalUi: boolean;
} {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, healthCares } = useSelector((state: RootState) => state);

  const casesResponse = patients?.fetchBasicDetails?.casesResponse;
  const patientResponse = patients?.fetchBasicDetails?.patientResponse;
  const visitInfoResponse = patients?.fetchBasicDetails?.visitInfoResponse;

  const healthCareId = casesResponse?.healthCareId;
  const schemeMetaDataId = casesResponse?.schemeMetaDataId;
  const portalName = normalizePortalName(
    casesResponse?.portalMetaDataNames?.[0]
  );
  const schemeName = String(
    casesResponse?.schemeMetaDataName ||
      casesResponse?.alternateName ||
      portalName ||
      "TPA"
  );

  useEffect(() => {
    if (!enabled || !healthCareId) return;
    dispatch(getAllSchemeSpecificLoginDetails({ healthCareId }));
  }, [dispatch, enabled, healthCareId]);

  const credentialMatch = useMemo(() => {
    const rows = Object.values(
      healthCares?.healthCareSchemeSpecificLoginDetails || {}
    );

    if (!rows.length) return null;

    if (schemeMetaDataId != null) {
      const byScheme = rows.find(
        (row) =>
          row?.schemeId != null &&
          Number(row.schemeId) === Number(schemeMetaDataId)
      );
      if (byScheme) return byScheme;
    }

    const hospitalLevel = rows.find(
      (row) => row?.schemeId == null || row?.schemeId === undefined
    );
    if (hospitalLevel) return hospitalLevel;

    if (rows.length === 1) return rows[0];

    return null;
  }, [healthCares?.healthCareSchemeSpecificLoginDetails, schemeMetaDataId]);

  const portalUse: InsurancePortalUse = useMemo(() => {
    const meta = parseInsuranceSetupMeta(credentialMatch?.alternateName);
    return derivePortalUse({
      metaPortalUse: meta.portalUse,
      loginUrl: credentialMatch?.loginUrl,
      schemeName,
    });
  }, [credentialMatch, schemeName]);

  const skipPortalUi = portalUse === "EMAIL";

  const loading =
    enabled &&
    !skipPortalUi &&
    !!healthCareId &&
    healthCares?.loading === true &&
    !credentialMatch;

  const config = useMemo((): TpaPortalConfig | null => {
    if (!enabled || skipPortalUi) return null;

    const loginUrl = credentialMatch?.loginUrl?.trim() || null;
    const fallbackUrl = getFallbackPortalUrl(portalName);
    const portalUrl = loginUrl || fallbackUrl;

    if (!portalUrl) return null;

    return {
      portalUrl,
      portalName: portalName || "PORTAL",
      schemeName,
      userName: credentialMatch?.userName
        ? String(credentialMatch.userName)
        : null,
      stageLabel: formatStageLabel(visitStageName),
      usedFallbackUrl: !loginUrl && !!fallbackUrl,
      portalUse,
      patientContext: {
        patientName: String(
          patientResponse?.fullName || casesResponse?.patientName || "—"
        ),
        uhid: String(patientResponse?.uhidNo || "—"),
        crNo: String(
          visitInfoResponse?.creditNumber ||
            visitInfoResponse?.opNumber ||
            "—"
        ),
        doa: String(visitInfoResponse?.doa || casesResponse?.doa || "—"),
        claimId: String(visitInfoResponse?.claimId || "—"),
      },
    };
  }, [
    enabled,
    skipPortalUi,
    credentialMatch,
    portalName,
    schemeName,
    visitStageName,
    patientResponse,
    visitInfoResponse,
    casesResponse,
    portalUse,
  ]);

  return { config, loading, skipPortalUi };
}
